import {
  CREATE_SERVICE_CASE,
  CREATE_SERVICE_CASE_SET_SUBMITTING,
  CREATE_SERVICE_CASE_UNSET_SUBMITTING,
} from 'actions';
import Notifier from 'common/helpers/Notifier';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import { coreApi } from 'src/api/config';
import { crtb2130MultipleClientNeeds, pays11589AtomicCaseCreation } from 'src/common/utils/FeatureFlags/flags';
import { feeSchedulePrograms as fspUtils } from '@unite-us/client-utils';
import { atomicOperations as atomicOperationBuilders } from '@unite-us/json-api-resources';
import { datadogLogs } from '@datadog/browser-logs';

const iso8601Date = (date) => moment.unix(date).toISOString();

const parseAuthorizationParams = (params) => {
  const authorizationRequest = get(params, 'authorization_request', {});
  const serviceStart = get(authorizationRequest, 'service_start', null);
  const amountRequested = get(authorizationRequest, 'amount_requested', null);

  if (serviceStart == null || amountRequested == null) {
    return {};
  }

  return authorizationRequest;
};

const getCaseState = (isOON, params) => {
  if (!isEmpty(parseAuthorizationParams(params))) {
    return 'pending_authorization';
  }
  return isOON ? 'off_platform' : 'managed';
};

const buildPersonConditions = (clientNeedIds) => (
  clientNeedIds?.length > 0 ?
    clientNeedIds.map((id) => ({ type: 'person_condition', id })) :
    undefined
);

const resolvePersonConditions = (clientNeedIds, state) => {
  if (crtb2130MultipleClientNeeds(state)) {
    return buildPersonConditions(clientNeedIds);
  }
  return clientNeedIds ? [{ type: 'person_condition', id: clientNeedIds }] : undefined;
};

const buildCaseData = (params, groupId, contactId, isOONCase, personConditions) => {
  const isOON = isOONCase || params.program.program_name === 'Referred Out of Network';
  return {
    program_enrollment_date: params.program.entry_date,
    description: params.service_case.description,
    state: getCaseState(isOON, params),
    provider: groupId,
    network: params.network_id,
    primary_worker: params.service_case.primary_worker_id,
    person: contactId,
    service: params.service_type_id,
    ...(isOON ? {} : {
      opened_date: moment.unix(params.program.entry_date).utc().format('YYYY-MM-DD'),
      program: params.program.program_id,
    }),
    ...(personConditions ? { person_conditions: personConditions } : {}),
  };
};

// Shared by both the legacy and atomic paths: builds the service_authorization
// payload (or null, if no authorization is being requested). `caseRef` is either
// a plain case id (legacy) or a `{ lid }` reference to a case created earlier in
// the same atomic request.
const buildAuthorizationData = async (params, contactId, caseRef, state) => {
  const authorizationRequest = parseAuthorizationParams(params);
  const feeScheduleProgramId = get(params, 'program.fee_schedule_program_id', null);

  // PAYS-10723
  datadogLogs.logger.info('buildAuthorizationData called', {
    caseRef,
    feeScheduleProgramId,
    authorizationRequest,
    rawAuthRequest: get(params, 'authorization_request'),
    isEmpty: isEmpty(authorizationRequest),
    willCreateAuth: !!(feeScheduleProgramId && !isEmpty(authorizationRequest)),
  });

  if (!feeScheduleProgramId || isEmpty(authorizationRequest)) {
    return null;
  }

  const {
    service_start: serviceStart,
    service_end: serviceEnd,
    amount_requested: amountRequested,
    insurance_id: insuranceId,
  } = authorizationRequest;

  const fspResponse = await coreApi.findRecord('fee_schedule_program', feeScheduleProgramId);
  const feeScheduleProgram = get(fspResponse, 'data.data');
  if (!feeScheduleProgram) {
    throw new Error('Contracted Program required');
  }
  const isCostBased = fspUtils.isCostPaymentType(feeScheduleProgram.payment_type);

  // PAYS-10723
  datadogLogs.logger.info('handleAuthorizationRequest: Creating service authorization', {
    insuranceId,
    caseRef,
  });

  return {
    state: 'requested',
    requested_starts_at: iso8601Date(serviceStart),
    requested_ends_at: iso8601Date(serviceEnd || serviceStart),
    ...(isCostBased ? { requested_cents: amountRequested } : { requested_unit_amount: amountRequested }),
    case: caseRef,
    fee_schedule_program: feeScheduleProgramId,
    person: contactId,
    requester: get(state, 'globalState.currentEmployee.id'),
    insurance: insuranceId,
  };
};

// Shared value builders for off-platform (OON) providers: a referral for a listed
// provider, or a note for a free-text ("CUSTOM") one.
const listedOONProviders = (oonProviders) => oonProviders
  .filter((oonProvider) => oonProvider.provider_type !== 'CUSTOM');

const customOONProviders = (oonProviders) => oonProviders
  .filter((oonProvider) => oonProvider.provider_type === 'CUSTOM' && !isEmpty(oonProvider.custom_name));

const buildOffPlatformReferralData = (oonProvider, providerId, networkId, caseRef) => ({
  state: 'off_platform',
  case: caseRef,
  sending_provider: providerId,
  sending_network: networkId,
  receiving_provider: oonProvider.provider_id,
  receiving_network: networkId,
  ...(oonProvider.program_id ? { receiving_program: oonProvider.program_id } : null),
});

const buildCustomProviderNoteData = (oonProvider, employeeId, caseRef) => ({
  subjectType: 'case',
  subject: caseRef,
  employee: employeeId,
  visibility: 'public',
  category: 'external_provider',
  text: `Referred off platform to ${oonProvider.custom_name}`,
});

const createCase = async (params, groupId, contactId, isOONCase, clientNeedIds, state) => {
  const personConditions = resolvePersonConditions(clientNeedIds, state);
  const caseData = buildCaseData(params, groupId, contactId, isOONCase, personConditions);

  return coreApi.createRecord('case', caseData);
};

const handleAuthorizationRequest = async (params, contactId, caseId, state) => {
  const authorizationData = await buildAuthorizationData(params, contactId, caseId, state);
  if (authorizationData) {
    await coreApi.createRecord('service_authorization', authorizationData);
  }
};

const uploadDocuments = async (caseId, dropzoneDocuments, attachedDocuments) => {
  await Promise.all([
    ...(dropzoneDocuments.length ? dropzoneDocuments.map((doc) => coreApi.uploadFile('case', caseId, doc)) : []),
    ...(attachedDocuments.length ? [
      coreApi.setRelationship(
        'case',
        caseId,
        { relationshipModel: 'file_upload', value: attachedDocuments.map((a) => a.document_id) },
      ),
    ] : []),
  ]);
};

const handleOONProviders = async (oonProviders, createdCase, caseId) => {
  await Promise.all(listedOONProviders(oonProviders)
    .map((oonProvider) => coreApi.createRecord(
      'referral',
      buildOffPlatformReferralData(oonProvider, createdCase.provider.id, createdCase.network.id, caseId),
    )));
};

const createCustomProviderNotes = async (oonProviders, caseId, currentEmployee) => {
  await Promise.all(customOONProviders(oonProviders)
    .map((oonProvider) => coreApi.createRecord(
      'note',
      buildCustomProviderNoteData(oonProvider, currentEmployee.id, caseId),
    )));
};

// Shared by both paths: hydrates person/consent onto the created case, dispatches the
// success actions, and fires the "Case Created" notification. Returns the created case.
const finalizeCreatedCase = async (dispatch, createdCase, status) => {
  const contact = await coreApi.populateRelationship('person', 'person', createdCase);
  await coreApi.populateRelationship('consent', 'consent', contact.person);

  dispatch({ type: CREATE_SERVICE_CASE_UNSET_SUBMITTING });
  dispatch({ type: CREATE_SERVICE_CASE, payload: { status, data: { data: createdCase } } });
  Notifier.dispatch(status, 'Case Created');

  return createdCase;
};

const processAtomicCase = async ({
  attachedDocuments,
  contactId,
  dropzoneDocuments,
  groupId,
  isOONCase,
  params,
  clientNeedIds,
  dispatch,
  state,
}) => {
  const caseRef = { lid: 'case' };
  const personConditions = resolvePersonConditions(clientNeedIds, state);
  const caseData = buildCaseData(params, groupId, contactId, isOONCase, personConditions);

  const operations = [atomicOperationBuilders.createRecord('case', caseData, caseRef)];

  const authorizationData = await buildAuthorizationData(params, contactId, caseRef, state);
  if (authorizationData) {
    operations.push(atomicOperationBuilders.createRecord('service_authorization', authorizationData));
  }

  const oonProviders = get(params, 'service_case.out_of_network_providers', []);
  const currentEmployee = get(state, 'globalState.currentEmployee');

  listedOONProviders(oonProviders)
    .forEach((oonProvider) => operations.push(atomicOperationBuilders.createRecord(
      'referral',
      buildOffPlatformReferralData(oonProvider, groupId, params.network_id, caseRef),
    )));

  customOONProviders(oonProviders)
    .forEach((oonProvider) => operations.push(atomicOperationBuilders.createRecord(
      'note',
      buildCustomProviderNoteData(oonProvider, currentEmployee.id, caseRef),
    )));

  if (attachedDocuments.length) {
    operations.push(atomicOperationBuilders.addToRelationship('case', caseRef, {
      relationship: 'file_uploads',
      relationshipModel: 'file_upload',
      value: attachedDocuments.map((a) => a.document_id),
    }));
  }

  const response = await coreApi.atomicOperations(operations);
  const atomicCase = get(response, ['data', 'atomic:results', 0, 'data'], null);
  if (!atomicCase?.id) {
    const missingCaseError = new Error('Atomic case creation did not return a case');
    missingCaseError.response = response;
    throw missingCaseError;
  }

  const createdCase = await finalizeCreatedCase(dispatch, atomicCase, response.status);
  const payload = { status: response.status, data: { data: atomicCase } };

  if (dropzoneDocuments.length) {
    try {
      await Promise.all(dropzoneDocuments.map((doc) => coreApi.uploadFile('case', createdCase.id, doc)));
    } catch (uploadError) {
      Notifier.handleErrors(uploadError);
    }
  }

  return payload;
};

export default function createServiceCase({
  attachedDocuments = [],
  contactId,
  dropzoneDocuments = [],
  groupId,
  isOONCase = false,
  params = {},
  clientNeedIds = [],
}) {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: CREATE_SERVICE_CASE_SET_SUBMITTING });

      const state = getState();

      // Validate insurance_id is present if authorization will be created
      const authorizationRequest = parseAuthorizationParams(params);
      const feeScheduleProgramId = get(params, 'program.fee_schedule_program_id', null);
      const willCreateAuthorization = !!(feeScheduleProgramId && !isEmpty(authorizationRequest));

      if (willCreateAuthorization && !authorizationRequest.insurance_id) {
        // PAYS-10723
        datadogLogs.logger.error('createServiceCase: Missing insurance_id for authorization', {
          authorizationRequest,
          feeScheduleProgramId,
        });
        throw new Error('Insurance is required when creating a case with authorization');
      }

      // PAYS-10723
      datadogLogs.logger.info('createServiceCase: Starting case creation', {
        contactId,
        groupId,
        isOONCase,
        hasAuthRequest: !!get(params, 'authorization_request'),
        hasFeeScheduleProgram: !!feeScheduleProgramId,
        willCreateAuthorization,
      });

      if (pays11589AtomicCaseCreation(state)) {
        return await processAtomicCase({
          attachedDocuments,
          contactId,
          dropzoneDocuments,
          groupId,
          isOONCase,
          params,
          clientNeedIds,
          dispatch,
          state,
        });
      }

      const payload = await createCase(params, groupId, contactId, isOONCase, clientNeedIds, state);
      const createdCase = await finalizeCreatedCase(dispatch, payload.data.data, payload.status);
      const { id: caseId } = createdCase;

      // PAYS-10723
      datadogLogs.logger.info('createServiceCase: Case created successfully, calling handleAuthorizationRequest', {
        caseId,
      });

      await handleAuthorizationRequest(params, contactId, caseId, state);
      await uploadDocuments(caseId, dropzoneDocuments, attachedDocuments);
      const oonProviders = get(params, 'service_case.out_of_network_providers', []);
      await handleOONProviders(oonProviders, createdCase, caseId);
      await createCustomProviderNotes(oonProviders, caseId, get(state, 'globalState.currentEmployee'));

      return payload;
    } catch (error) {
      // PAYS-10723
      datadogLogs.logger.error('createServiceCase: Error occurred when creating case: ', error);
      dispatch({ type: CREATE_SERVICE_CASE_UNSET_SUBMITTING });
      Notifier.handleErrors(error);
      return error;
    }
  };
}
